import { Dropdown, FormGroup, Stack, TextInput } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { isEmail } from './emailModel';
import { listEmailAccounts, messagingAccountsKey, type EmailSender } from './messagingApi';

type AccountChoice = { id: string | null; name: string };

/**
 * Who an email is from (ADR 0125): the SMTP profile it goes through (one of the company's email accounts for the brand) and the
 * sender's name, address and reply-to. Each left empty uses the default named by `defaultLabel`.
 */
export function EmailSenderFields({
  id,
  legend,
  brandId,
  value,
  onChange,
  submitted,
  defaultLabel,
  disabled,
}: {
  id: string;
  legend: string;
  brandId: string;
  value: EmailSender;
  onChange(next: EmailSender): void;
  submitted: boolean;
  defaultLabel: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const accounts = useQuery({ queryKey: messagingAccountsKey, queryFn: listEmailAccounts });
  const mine = (accounts.data ?? []).filter((a) => a.isActive && (!a.brandId || a.brandId === brandId));
  const items: AccountChoice[] = [{ id: null, name: defaultLabel }, ...mine.map((a) => ({ id: a.id, name: a.name }))];
  // An account no longer offered (inactive, or another brand's) still shows, so saving doesn't silently drop it.
  const selected = items.find((i) => i.id === (value.accountId ?? null)) ?? { id: value.accountId ?? null, name: t('messaging.email.sender.accountGone') };
  const badFrom = submitted && !!value.fromAddress?.trim() && !isEmail(value.fromAddress.trim());
  const badReply = submitted && !!value.replyTo?.trim() && !isEmail(value.replyTo.trim());

  return (
    <FormGroup legendText={legend}>
      <Stack gap={5}>
        <Dropdown<AccountChoice>
          id={`${id}-account`}
          titleText={t('messaging.email.sender.account')}
          helperText={accounts.data && mine.length === 0 ? t('messaging.email.sender.noAccounts') : t('messaging.email.sender.accountHelp')}
          label={defaultLabel}
          items={items}
          itemToString={(i) => i?.name ?? ''}
          selectedItem={selected}
          disabled={disabled}
          onChange={({ selectedItem }) => onChange({ ...value, accountId: selectedItem?.id ?? null })}
        />
        <div className="pos-email-sender">
          <TextInput
            id={`${id}-from-name`}
            labelText={t('messaging.email.sender.fromName')}
            placeholder={defaultLabel}
            value={value.fromName ?? ''}
            maxLength={100}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, fromName: e.target.value })}
          />
          <TextInput
            id={`${id}-from-address`}
            type="email"
            labelText={t('messaging.email.sender.fromAddress')}
            placeholder={defaultLabel}
            value={value.fromAddress ?? ''}
            maxLength={254}
            disabled={disabled}
            invalid={badFrom}
            invalidText={t('messaging.email.sender.emailInvalid')}
            onChange={(e) => onChange({ ...value, fromAddress: e.target.value })}
          />
          <TextInput
            id={`${id}-reply-to`}
            type="email"
            labelText={t('messaging.email.sender.replyTo')}
            placeholder={defaultLabel}
            value={value.replyTo ?? ''}
            maxLength={254}
            disabled={disabled}
            invalid={badReply}
            invalidText={t('messaging.email.sender.emailInvalid')}
            onChange={(e) => onChange({ ...value, replyTo: e.target.value })}
          />
        </div>
      </Stack>
    </FormGroup>
  );
}
